import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import EventAvailableIcon from '@mui/icons-material/EventAvailable';
import { BarChart3, FileText, Calendar, TrendingUp, Plus,  AlertTriangle } from 'lucide-react';
import Overview from '../../components/leaves/Overview';
import Requests from '../../components/tables/LeaveRequests';
import CalendarView from '../../components/leaves/Calendar';
import Analytics from '../../components/leaves/Analytics';
import DayDetailsModal from '../../components/leaves/DayDetailsModal';
import {  CircularProgress, ThemeProvider, useMediaQuery, Fab, Typography, Box } from '@mui/material';
import PictureAsPdfIcon from '@mui/icons-material/PictureAsPdf'; 
import { IconUserCode } from '@tabler/icons-react';

import LeaveRequestPage from './leaveRequestPage';
import EmployeeCard from '../../components/lists/EmplyeeList'; 
import PageHeader from '../../components/headers/Header';
import LeaveRequestForm from '../../components/Forms/LeaveRequestForm';
import LeaveDashboard from '../../components/dash/LeaveDash'; 
import '../../components/leaves/Leaves.css';
import { monthNames } from '../../components/leaves/constants';
import { getPendingRequestsOlderThan15Days, getApprovedLeaves, getAllLeaves, getSickLeaves, approveLeave, rejectLeave, getLeavesPerDepartment, getMyLeaves } from '../../services/leavesAPIs';
import {getAllEmployees} from '../../services/EmployeeService';
import Employee from '../../assets/employee.gif';
import { exportToPdf } from '../../utils/exportToPdf'; 

import FormHeader from '../../components/headers/FormHeader.jsx';

import { useRef } from 'react';

const NotEmployee = localStorage.getItem('userRole')!=='EMPLOYEE';

const AddLeaveRequest = () => {
  const [IsEmployee,setIsEmployee]=useState(localStorage.getItem('userRole')==='EMPLOYEE');
  
  const modalRef = useRef(null);
  const rejectionDialogRef = useRef(null);
  const isLargeScreen = useMediaQuery('(min-width: 1600px)');
  const navigate = useNavigate();

  const lb='handleWithEmployeeId';
  const [selectedEmployeeId, setSelectedEmployeeId] = useState(null);
  const [employees, setEmployees] = useState([]);
  const [leavesData, setLeavesData] = useState([]);
  const [urgentRequests, setUrgentRequests] = useState([]);
  const [hasUrgentRequests, setHasUrgentRequests] = useState(false);
  const [currentDate] = useState(new Date('2025-06-19T10:26:00+01:00'));
  const [currentMonth, setCurrentMonth] = useState(new Date().getMonth());
  const [currentYear, setCurrentYear] = useState(currentDate.getFullYear());
  const [selectedDay, setSelectedDay] = useState(null);
  const [selectedLeave, setSelectedLeave] = useState(null);

  const [activeTab, setActiveTab] = useState(IsEmployee ? 'EmpRequest' : 'newOverview');
   
  const [filters, setFilters] = useState({
    type: '',
    status: '',
    department: '',
    year: new Date().getFullYear(),
    month: new Date().getMonth() + 1,
    search: ''
  });

  const [dashboardFilters, setDashboardFilters] = useState({
    periodType: 'year', // or 'month'
    periodValue: new Date().getFullYear().toString(), // e.g., "2025" or "2025-06"
    office: 'All',
  });
  const [showEmployeeCard, setShowEmployeeCard] = useState(false);

  // PDF states
  const [isExporting, setIsExporting] = useState(false);
  const [exportError, setExportError] = useState(null);
  const [snackbarOpen, setSnackbarOpen] = useState(false);
  const [loading, setLoading] = useState(true);

  // Data fetching - update the useEffect
  const fetchData = async () => {
    setLoading(true);
    let allLeavesData = [];
    try {
      if (IsEmployee) {
        allLeavesData = await getMyLeaves();
      } else {
        allLeavesData = await getAllLeaves();
      }
      const urgentRequestsData = await getPendingRequestsOlderThan15Days();
        
      setLeavesData(allLeavesData);
      setUrgentRequests(urgentRequestsData);
      setHasUrgentRequests(urgentRequestsData.length > 0);

      const allEmployees = await getAllEmployees();
      setEmployees(allEmployees);
        
      console.log("Urgent Requests:", urgentRequestsData);
      console.log("All Leaves:", allLeavesData);
      console.log("All Employees:", allEmployees);

    } catch (error) {
      console.error("Error fetching data:", error);
    } finally {
      setLoading(false); // Set loading to false
    }
  };

  useEffect(() => {
    fetchData();
  }, [IsEmployee]);

  useEffect(() => {
    const handleClickOutside = (event) => {
      if (
        selectedLeave &&
        modalRef.current &&
        !modalRef.current.contains(event.target)&&
      (!rejectionDialogRef.current || !rejectionDialogRef.current.contains(event.target))
    ) {
        setSelectedLeave(null);
      }
      if (
        selectedDay &&
        modalRef.current &&
        !modalRef.current.contains(event.target)
      ) {
        setSelectedDay(null);
      }
    };

    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, [selectedLeave, selectedDay]);

  useEffect(() => {
    if (IsEmployee) {
      setActiveTab('EmpRequest'); // or 'absences', depending on your desired default
    } else if (!IsEmployee) {
      setActiveTab('newOverview'); // Set to 'overview' when switching to non-employee
    }
  }, [IsEmployee]);
  
  const handleEmployeeSelect = (id) => {
    setSelectedEmployeeId(id);
    setShowEmployeeCard(false);
    console.log('Selected employee ID:', id);
  };

  const handleStatusCardClick = (status) => {
  const { periodType, periodValue } = dashboardFilters;

  let year, month;
  if (periodType === 'year') {
    year = parseInt(periodValue);
    month = null;
  } else {
    const [yearStr, monthStr] = periodValue.split('-');
    year = parseInt(yearStr, 10);
    month = parseInt(monthStr, 10); // Must parse as int!
  }

  setFilters(prev => ({
    ...prev,
    status,
    department: '',
    type: '',
    year,      // <-- ADD THIS
    month,     // <-- ADD THIS
    //office: dashboardFilters.office, // Optional sync
    office: dashboardFilters.office === 'All' ? '' : dashboardFilters.office,
  }));
  setActiveTab('requests');
};



  return (
    <div>
         <FormHeader
        title={'Submit new leave request'}
        icon={EventAvailableIcon}
        onBack={() => {navigate(-1)}}
      />

         <Typography variant="h4" gutterBottom sx={{ mb: 1, ml:2.5 , fontWeight: 'bold', color: '#c01557' }}>
          👤 Don't forget to choose the employee
         </Typography>
        
        {isLargeScreen ? (
        <div style={{display: 'flex', gap: '16px'}}>
          <EmployeeCard employees={employees}  onSelectEmployee={handleEmployeeSelect} itemsPerPage={6}/>
          <LeaveRequestForm IsEmployee={IsEmployee} lb={lb} id={selectedEmployeeId} employees={employees} fetchData={fetchData}/>
        </div>
        ) : (
          <div style={{display: 'flex', gap: '16px'}}>
            <Fab
                onClick={() => setShowEmployeeCard(!showEmployeeCard)}
                sx={{
                  position: 'fixed',
                  bottom: 20,
                  right: 20,
                  zIndex: 1000,
                  backgroundColor: 'var(--card-bg, white)',
                  boxShadow: 3,
                }}
                  >
                  <img
                    src={Employee}
                    alt="Employee"
                    style={{ width: '40px', height: '35px', borderRadius:'5px' }} // Adjust size as needed
                    />   
                </Fab>
                {showEmployeeCard && (
                  <Box
                    sx={{
                      position: 'fixed',
                      bottom: 15,
                      right: 100,
                      width: '90%',
                      maxWidth: 500,
                      zIndex: 1000,
                      boxShadow: 3,
                      borderRadius: 2,
                      backgroundColor: 'var(--card-bg, white)',
                      p: 2,
                  }}
                  >
                     <EmployeeCard employees={employees}  onSelectEmployee={handleEmployeeSelect} itemsPerPage={2}/>
                  </Box>
                      )}

                <LeaveRequestForm IsEmployee={IsEmployee} lb={lb} id={selectedEmployeeId} employees={employees} fetchData={fetchData}/>
            </div>

        )}
       
    </div>
  );
};

export default AddLeaveRequest;